import { useEffect, useRef } from 'react';
import { Icon, StatusPill } from '../common';
import type { BookingStatus } from '../bookings/types';
import { splitHighlight, TYPE_META, type SearchRecord } from './searchTypes';

// One search row — icon tile, name-over-sub with every query hit marked, and a
// trailing chip (booking → its status colour; else the type label). Used as a
// standalone/recent row, a cluster header (`variant="header"`), or a nested
// household member (`variant="member"`, smaller). Ported from the prototype's
// rowHTML onto the real Icon + tokens.

function Marked({ text, q }: { text: string; q: string }) {
  return (
    <>
      {splitHighlight(text, q).map((seg, i) =>
        seg.mark ? <mark key={i}>{seg.text}</mark> : <span key={i}>{seg.text}</span>,
      )}
    </>
  );
}

function Chip({ rec }: { rec: SearchRecord }) {
  // Booking status → the app's ONE global StatusPill (icon + tone + label from
  // the single source, bookings/constants). Read-only here (no onClick).
  if (rec.type === 'booking' && rec.status) {
    return <StatusPill status={rec.status as BookingStatus} className="sm" />;
  }
  return <span className="gs-rtype">{TYPE_META[rec.type].label}</span>;
}

export function SearchRow({
  rec,
  q,
  variant = 'row',
  active = false,
  onOpen,
}: {
  rec: SearchRecord;
  q: string;
  variant?: 'row' | 'header' | 'member';
  active?: boolean;
  onOpen: (rec: SearchRecord) => void;
}) {
  const meta = TYPE_META[rec.type];
  const isMember = variant === 'member';
  const ref = useRef<HTMLDivElement>(null);
  // Keep the keyboard-active row in view as the user arrows through.
  useEffect(() => {
    if (active) ref.current?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const cls = [
    'gs-row',
    variant === 'header' ? 'gs-cl-head' : '',
    variant === 'member' ? 'gs-member' : '',
    active ? 'active' : '',
  ]
    .filter(Boolean)
    .join(' ');

  // A listbox option (not a <button>) so the global StatusPill (itself a button)
  // nests validly; keyboard nav is owned by the input, mouse by this onClick.
  return (
    <div
      className={cls}
      data-type={rec.type}
      role="option"
      aria-selected={active}
      ref={ref}
      onClick={() => onOpen(rec)}
    >
      <span className="gs-ic">
        <Icon name={meta.icon} size={isMember ? 15 : 17} />
      </span>
      <span className="gs-rtext">
        <span className="gs-rname">
          <Marked text={rec.name} q={q} />
        </span>
        <span className="gs-rsub">
          <Marked text={rec.sub} q={q} />
        </span>
      </span>
      <Chip rec={rec} />
    </div>
  );
}
